import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useProfileStore } from '../../stores/useDataStore'; // 🟢 1. Importamos el store del perfil
import { getMediaUrl } from '../../utils/imageUtils';

const ImageModal = ({
  images,
  currentIndex,
  onClose,
  onNext,
  onPrev,
  onRemove,
  isMyProfile,
}) => {
  // Estados para controlar el menú "Más" y el mensaje del botón "Compartir"
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [shareTooltip, setShareTooltip] = useState('Compartir');

  // 🟢 2. Obtenemos las acciones necesarias del store
  const updateAvatarFromAlbum = useProfileStore((state) => state.updateAvatarFromAlbum);
  const updateCoverFromAlbum = useProfileStore((state) => state.updateCoverFromAlbum);

  // Referencia para el menú "Más" para detectar clics fuera
  const moreMenuRef = useRef(null);

  // No renderiza si no hay imágenes o si el índice es inválido
  if (currentIndex === null || !images || !images[currentIndex]) {
    return null;
  }

  // 🟢 CORRECCIÓN: Aseguramos que currentImage sea un objeto válido para evitar errores.
  // La URL de la imagen ahora se obtiene de la propiedad `url` o `path`.
  const currentImageObject = images[currentIndex];
  const currentImageUrl = currentImageObject.url || currentImageObject.path;
  const resolvedImageUrl = getMediaUrl(currentImageUrl);

  // Efecto para cerrar el menú "Más" si se hace clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setIsMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Efecto para manejar la navegación y cierre con teclado (Escape, flechas)
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowRight') onNext();
      else if (event.key === 'ArrowLeft') onPrev();
    },
    [onClose, onNext, onPrev]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Función para manejar la eliminación de la imagen
  const handleDelete = () => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta imagen?')) {
      onRemove(currentImageObject.publicId || currentImageObject._id || currentImageObject.id);
      onClose(); // Cierra el modal después de eliminar
    }
  };

  // Función para manejar el botón "Compartir" (copia el enlace al portapapeles)
  const handleShare = () => {
    // Asume que la URL completa de la imagen es window.location.origin + currentImage.url
    // window.location.origin es "http://localhost:5173" o tu dominio
    navigator.clipboard.writeText(window.location.origin + resolvedImageUrl)
      .then(() => {
        setShareTooltip('¡Enlace copiado!');
        setTimeout(() => setShareTooltip('Compartir'), 2000); // Resetea el mensaje después de 2 segundos
      })
      .catch((err) => console.error('Error al copiar enlace: ', err));
  };

  // Función para manejar la descarga de la imagen
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = resolvedImageUrl;
    link.download = resolvedImageUrl.substring(
      resolvedImageUrl.lastIndexOf('/') + 1
    ); // Nombre del archivo basado en la URL
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsMoreMenuOpen(false); // Cierra el menú después de la acción
  };
  
  // 🟢 4. Implementamos las funciones para establecer como avatar y portada
  const handleSetAsAvatar = () => {
    updateAvatarFromAlbum(currentImageUrl);
    setIsMoreMenuOpen(false);
    onClose();
  };

  const handleSetAsCover = () => {
    updateCoverFromAlbum(currentImageUrl);
    setIsMoreMenuOpen(false);
    onClose();
  };

  // Opciones para el menú "Más"
  let moreOptions = [
    { name: 'Descargar', icon: 'fas fa-download', action: handleDownload, requiresAuth: false },
    { name: 'Denunciar', icon: 'fas fa-flag', action: () => { alert('Imagen denunciada (simulación).'); setIsMoreMenuOpen(false); }, requiresAuth: false },
  ];

  // 🟢 5. Añadimos las opciones de perfil solo si es nuestro perfil
  if (isMyProfile) {
    moreOptions.unshift(
      {
      name: 'Establecer como foto de perfil',
      icon: 'fas fa-user-circle',
      action: handleSetAsAvatar,
      requiresAuth: true
      },
      {
        name: 'Establecer como foto de portada',
        icon: 'fas fa-image',
        action: handleSetAsCover,
        requiresAuth: true
      });
  }

  return (
    // Contenedor principal del modal
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-80"
      onClick={onClose}
    >
      {/* Contenedor del contenido del modal */}
      <div
        className="relative bg-white rounded-lg shadow-xl overflow-hidden max-w-2xl w-full mx-4 flex flex-col"
        onClick={(e) => e.stopPropagation()} // Evita que el clic dentro cierre el modal
      >
        {/* Botón de cerrar el modal */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-800 hover:text-gray-900 z-20 bg-white bg-opacity-75 rounded-full p-2"
          aria-label="Cerrar"
        >
          <i className="fas fa-times text-lg"></i>
        </button>

        {/* Contenedor principal de la imagen con botones de navegación */}
        <div className="relative flex-grow flex items-center justify-center bg-gray-900 p-2">
          {/* Botón para navegar a la imagen anterior */}
          {images.length > 1 && (
            <button
              onClick={onPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-white bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 z-10"
              aria-label="Anterior"
            >
              <i className="fas fa-chevron-left text-xl"></i>
            </button>
          )}

          {/* La imagen actual */}
          <img
            src={resolvedImageUrl}
            alt="Imagen en modal"
            className="max-h-[75vh] max-w-full object-contain rounded-lg" // Tamaño de la imagen dentro del modal
          />

          {/* Botón para navegar a la siguiente imagen */}
          {images.length > 1 && (
            <button
              onClick={onNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 z-10"
              aria-label="Siguiente"
            >
              <i className="fas fa-chevron-right text-xl"></i>
            </button>
          )}
        </div>

        {/* Pie de página con las acciones de la imagen */}
        <footer className="bg-gray-500 border-t border-gray-800 p-3 flex justify-between items-center">
          <div className="flex space-x-4">
            {/* Botón Compartir */}
            <button
              onClick={handleShare}
              className="text-gray-300 p-btn-p-2 hover:text-blue-600 transition-colors"
              title={shareTooltip} // Muestra el tooltip dinámico
            >
              <i className="fas fa-share-alt text-xl"></i>
            </button>
            {/* Botón Etiquetar (funcionalidad simulada) */}
            <button
              onClick={() =>
                alert('Funcionalidad para etiquetar (próximamente).')
              }
              className="text-gray-300 p-btn-p-2 hover:text-blue-600 transition-colors"
              title="Etiquetar"
            >
              <i className="fas fa-tag text-xl"></i>
            </button>
          </div>
          <div className="flex space-x-4">
            {/* Botón Eliminar (solo visible si es tu perfil) */}
            {isMyProfile && (
              <button
                onClick={handleDelete}
                className="text-gray-600 hover:text-red-600 transition-colors"
                title="Eliminar"
              >
                <i className="fas fa-trash-alt text-xl"></i>
              </button>
            )}
            {/* Botón "Más" con menú desplegable */}
            <div className="relative" ref={moreMenuRef}>
              <button
                onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                className="text-gray-600 p-btn-p-2 hover:text-blue-600 transition-colors"
                title="Más opciones"
              >
                <i className="fas fa-ellipsis-h text-xl"></i>
              </button>
              {/* Menú desplegable */}
              {isMoreMenuOpen && (
                <div className="absolute bottom-full right-0 mb-2 w-56 bg-white rounded-md shadow-lg z-30 border border-gray-100">
                  <ul className="py-1">
                    {moreOptions.map((option) => (
                      <li key={option.name}>
                        <button
                          onClick={option.action}
                          className="flex items-center p-btn-p-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <i
                            className={`${option.icon} w-6 mr-2 text-gray-500`}
                          ></i>
                          <span>{option.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ImageModal;
